// =============================================================================
// clientes/src/paginas/Pedidos.tsx — meus pedidos e a situação de cada um.
//
// A RLS entrega SÓ os pedidos deste parceiro. Cancelar só em "novo" — depois
// que a loja confirmou (e talvez já encomendou), desfazer é telefone.
// =============================================================================
import { useState } from 'react';
import { useLocation } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SituacaoPedidoB2B } from '@core/types';
import Aviso from '../componentes/Aviso';
import Carregando from '../componentes/Carregando';
import { cancelarPedido, meusPedidos } from '../lib/consultas';
import { brl, dataHoraBR, qtd } from '../lib/formato';
import { mensagemDeErro } from '../lib/supabase';

const SITUACAO: Record<SituacaoPedidoB2B, { rotulo: string; classe: string }> = {
  novo: { rotulo: 'Aguardando a loja', classe: 'text-alerta' },
  confirmado: { rotulo: 'Confirmado', classe: 'text-ok' },
  pronto: { rotulo: 'Pronto para retirar/entregar', classe: 'text-ok' },
  entregue: { rotulo: 'Entregue', classe: 'text-dim' },
  cancelado: { rotulo: 'Cancelado', classe: 'text-erro' },
};

export default function Pedidos() {
  const cliente = useQueryClient();
  const { state } = useLocation() as { state?: { confirmado?: boolean } };
  const [aberto, setAberto] = useState<string | null>(null);
  const [cancelando, setCancelando] = useState<string | null>(null);

  const pedidos = useQuery({ queryKey: ['meus-pedidos'], queryFn: meusPedidos });
  const cancelar = useMutation({
    mutationFn: (id: string) => cancelarPedido(id),
    onSuccess: () => {
      setCancelando(null);
      cliente.invalidateQueries({ queryKey: ['meus-pedidos'] });
    },
  });

  return (
    <div className="space-y-4">
      {state?.confirmado && (
        <Aviso tipo="ok">
          Pedido enviado! A loja recebe na hora e confirma em seguida —
          acompanhe a situação aqui.
        </Aviso>
      )}
      {pedidos.error ? <Aviso tipo="erro">{mensagemDeErro(pedidos.error)}</Aviso> : null}
      {cancelar.error ? <Aviso tipo="erro">{mensagemDeErro(cancelar.error)}</Aviso> : null}
      {pedidos.isPending && <Carregando texto="Buscando seus pedidos" />}

      <ul className="space-y-2">
        {(pedidos.data ?? []).map((p) => {
          const s = SITUACAO[p.situacao];
          return (
            <li key={p.id} className="cartao p-3">
              <button type="button" className="w-full text-left"
                      onClick={() => setAberto(aberto === p.id ? null : p.id)}>
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-bold">
                      <span className="font-mono">{p.codigo}</span>
                    </p>
                    <p className="text-xs text-mute">{dataHoraBR(p.created_at)}</p>
                  </div>
                  <div className="text-right">
                    <p className="tabular-nums text-base font-black">{brl(Number(p.total))}</p>
                    <p className={`text-xs font-semibold ${s.classe}`}>{s.rotulo}</p>
                  </div>
                </div>
              </button>

              {aberto === p.id && (
                <div className="mt-3 space-y-3 border-t border-edge pt-3">
                  <ul className="space-y-1">
                    {p.partner_order_items.map((i) => (
                      <li key={i.id} className="flex items-baseline justify-between gap-2 text-sm">
                        <span className="min-w-0">
                          {i.descricao}
                          {!i.pronta_entrega && (
                            <span className="ml-1 text-xs text-alerta">sob encomenda</span>
                          )}
                        </span>
                        <span className="shrink-0 tabular-nums text-xs">
                          {qtd(Number(i.quantidade))} × {brl(Number(i.preco_unit))}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {p.observacao && <p className="text-xs text-mute">Obs.: {p.observacao}</p>}

                  {p.situacao === 'novo' && (
                    cancelando === p.id ? (
                      <div className="space-y-2 border border-erro/40 bg-erro/5 p-3">
                        <p className="text-xs text-mute">Cancelar este pedido?</p>
                        <div className="flex gap-2">
                          <button type="button" className="botao-secundario flex-1 py-1.5 text-xs"
                                  onClick={() => setCancelando(null)}>Voltar</button>
                          <button type="button" className="botao-primario flex-1 py-1.5 text-xs"
                                  disabled={cancelar.isPending}
                                  onClick={() => cancelar.mutate(p.id)}>
                            Cancelar pedido
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button type="button" className="text-xs text-erro underline"
                              onClick={() => setCancelando(p.id)}>
                        cancelar pedido
                      </button>
                    )
                  )}
                  {p.situacao !== 'novo' && p.situacao !== 'cancelado' && (
                    <p className="text-xs text-dim">
                      Precisa mudar algo? Ligue para a loja — pedido confirmado
                      não se cancela por aqui.
                    </p>
                  )}
                </div>
              )}
            </li>
          );
        })}
        {(pedidos.data ?? []).length === 0 && !pedidos.isPending && (
          <li className="text-sm text-dim">Nenhum pedido ainda — o primeiro sai do catálogo.</li>
        )}
      </ul>
    </div>
  );
}
